import { useState } from 'react';
import { Alert, Button, Group, NumberInput, SegmentedControl, Stack, Table, Text, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle, IconTrash } from '@tabler/icons-react';
import type { SiloCalibrationMethod, SiloDensitySource, UpsertSiloCalibrationInput } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { useIsAdmin } from '../../../auth/permissions';
import { parseNum } from '../../../lib/parseNum';
import { useUpsertSiloCalibration } from '../api';
import type { SiloCalibrationDto, StrappingPoint } from '../types';

interface StrappingRow {
  levelM: number | string;
  volumeM3: number | string;
}

function isStrappingSorted(points: StrappingRow[]): boolean {
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    if (!prev || !curr) return false;
    const prevLevel = parseNum(prev.levelM);
    const currLevel = parseNum(curr.levelM);
    if (prevLevel === null || currLevel === null) return false;
    if (currLevel <= prevLevel) return false;
  }
  return true;
}

function toStrappingRows(points: StrappingPoint[] | null): StrappingRow[] {
  return points?.map((p) => ({ levelM: p.levelM, volumeM3: p.volumeM3 })) ?? [];
}

/** Silo calibration create/edit form (BUILD_SPEC.md §5.2, §5.6; sole owner of
 * this config screen per plan.md's QC fix #3) — method LINEAR (height + full
 * volume) or STRAPPING (a level→volume breakpoint table), plus a density
 * source (product bulk density, or a per-silo override). Mirrors the exact
 * cross-field validation `@zebra/core`'s `upsertSiloCalibrationRequestSchema`
 * enforces server-side (packages/core/src/schemas/config.ts), so a mistake is
 * caught here before the round-trip. Plain `useState` (not `@mantine/form`) for
 * the dynamic strapping-table rows — keeps every field's type explicit rather
 * than relying on dynamic dot-path inference for a nested array. */
export function CalibrationForm({
  locationId,
  calibration,
  onSaved,
}: {
  locationId: string;
  calibration: SiloCalibrationDto | null;
  onSaved: () => void;
}) {
  const isAdmin = useIsAdmin();
  const upsert = useUpsertSiloCalibration(locationId);

  const [method, setMethod] = useState<SiloCalibrationMethod>(calibration?.method ?? 'LINEAR');
  const [heightM, setHeightM] = useState<number | string>(calibration?.heightM ? Number(calibration.heightM) : '');
  const [fullVolumeM3, setFullVolumeM3] = useState<number | string>(
    calibration?.fullVolumeM3 ? Number(calibration.fullVolumeM3) : '',
  );
  const [strapping, setStrapping] = useState<StrappingRow[]>(toStrappingRows(calibration?.strapping ?? null));
  const [densitySource, setDensitySource] = useState<SiloDensitySource>(calibration?.densitySource ?? 'PRODUCT');
  const [densityOverrideKgM3, setDensityOverrideKgM3] = useState<number | string>(
    calibration?.densityOverrideKgM3 ? Number(calibration.densityOverrideKgM3) : '',
  );

  const parsedHeightM = parseNum(heightM);
  const parsedFullVolumeM3 = parseNum(fullVolumeM3);
  const parsedDensityOverrideKgM3 = parseNum(densityOverrideKgM3);

  const linearError =
    method === 'LINEAR' && (parsedHeightM === null || parsedFullVolumeM3 === null)
      ? 'Height and full volume are both required for LINEAR calibration'
      : null;
  const strappingError =
    method === 'STRAPPING' && strapping.length === 0
      ? 'Add at least one level→volume point'
      : method === 'STRAPPING' && !isStrappingSorted(strapping)
        ? 'Points must be sorted, strictly ascending by level (m)'
        : null;
  const densityError =
    densitySource === 'OVERRIDE' && parsedDensityOverrideKgM3 === null ? 'Required when density source is Override' : null;
  const hasError = Boolean(linearError || strappingError || densityError);

  function updateRow(index: number, field: keyof StrappingRow, value: number | string) {
    setStrapping((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  }

  function removeRow(index: number) {
    setStrapping((rows) => rows.filter((_, i) => i !== index));
  }

  function addRow() {
    setStrapping((rows) => [...rows, { levelM: '', volumeM3: '' }]);
  }

  function handleSubmit() {
    if (hasError) return;
    const payload: UpsertSiloCalibrationInput = {
      method,
      heightM: method === 'LINEAR' && parsedHeightM !== null ? parsedHeightM.toFixed(3) : null,
      fullVolumeM3: method === 'LINEAR' && parsedFullVolumeM3 !== null ? parsedFullVolumeM3.toFixed(3) : null,
      strapping:
        method === 'STRAPPING'
          ? strapping.map((p) => ({ levelM: Number(p.levelM), volumeM3: Number(p.volumeM3) }))
          : null,
      densitySource,
      densityOverrideKgM3:
        densitySource === 'OVERRIDE' && parsedDensityOverrideKgM3 !== null ? parsedDensityOverrideKgM3.toFixed(3) : null,
    };
    upsert.mutate(payload, { onSuccess: onSaved });
  }

  return (
    <Stack gap="md">
      <Alert color="graphite" variant="light" icon={<IconInfoCircle size={18} />}>
        Calibration converts every future level reading into an estimated tonnage for this silo — get it right once here,
        and every reading after it is accurate.
      </Alert>

      <div>
        <Text size="sm" fw={500} mb={4}>
          Method
        </Text>
        <Text c="dimmed" size="xs" mb={4}>
          Linear assumes a simple cylinder — height and full volume. Strapping is for an irregular shape, calibrated
          from a level→volume table instead.
        </Text>
        <SegmentedControl
          fullWidth
          value={method}
          onChange={(v) => setMethod(v as SiloCalibrationMethod)}
          data={[
            { value: 'LINEAR', label: 'Linear (cylinder)' },
            { value: 'STRAPPING', label: 'Strapping table' },
          ]}
        />
      </div>

      {method === 'LINEAR' ? (
        <Group grow>
          <NumberInput
            label="Height (m)"
            description="Full internal height, floor to the fill line"
            min={0}
            decimalScale={3}
            value={heightM}
            onChange={setHeightM}
          />
          <NumberInput
            label="Full volume (m³)"
            description="Volume when completely full to that height"
            min={0}
            decimalScale={3}
            value={fullVolumeM3}
            onChange={setFullVolumeM3}
          />
        </Group>
      ) : (
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Level → volume points (ascending by level)
          </Text>
          <Table.ScrollContainer minWidth={640}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Level (m)</Table.Th>
                  <Table.Th>Volume (m³)</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {strapping.map((row, index) => (
                  <Table.Tr key={index}>
                    <Table.Td>
                      <NumberInput
                        min={0}
                        decimalScale={3}
                        value={row.levelM}
                        onChange={(v) => updateRow(index, 'levelM', v)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        min={0}
                        decimalScale={3}
                        value={row.volumeM3}
                        onChange={(v) => updateRow(index, 'volumeM3', v)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label="Remove this level→volume point">
                        <Button
                          variant="subtle"
                          color="red"
                          size="compact-sm"
                          aria-label="Remove level→volume point"
                          onClick={() => removeRow(index)}
                        >
                          <IconTrash size={16} />
                        </Button>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Group>
            <Button variant="default" size="compact-sm" onClick={addRow}>
              Add point
            </Button>
          </Group>
        </Stack>
      )}

      {(linearError ?? strappingError) && (
        <Alert color="amber" icon={<IconAlertTriangle size={18} />}>
          {linearError ?? strappingError}
        </Alert>
      )}

      <div>
        <Text size="sm" fw={500} mb={4}>
          Density source
        </Text>
        <SegmentedControl
          fullWidth
          value={densitySource}
          onChange={(v) => setDensitySource(v as SiloDensitySource)}
          data={[
            { value: 'PRODUCT', label: 'Product bulk density' },
            { value: 'OVERRIDE', label: 'Override for this silo' },
          ]}
        />
      </div>
      {densitySource === 'OVERRIDE' && (
        <NumberInput
          label="Density override (kg/m³)"
          description="Replaces the product's bulk density for this silo only — use for a settling or packing difference specific to this silo"
          min={0}
          decimalScale={3}
          value={densityOverrideKgM3}
          onChange={setDensityOverrideKgM3}
          error={densityError}
        />
      )}

      {upsert.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {upsert.error instanceof ApiRequestError ? upsert.error.message : upsert.error?.message}
        </Alert>
      )}

      <Group justify="flex-end">
        <Tooltip label="Requires Admin role" disabled={isAdmin}>
          <span>
            <Button onClick={handleSubmit} disabled={!isAdmin} loading={upsert.isPending}>
              Save calibration
            </Button>
          </span>
        </Tooltip>
      </Group>
    </Stack>
  );
}
